/**
 * Grips4Less build cart helper
 * ----------------------------
 * Works with snippets/cart-items.liquid (cart page + cart drawer).
 *
 * 1. "Remove build" button (.js-remove-build): removes every line that shares
 *    the build's _build_id in ONE /cart/update.js call, keyed by line key.
 * 2. Sync: on load and when the tab regains focus, removes builds whose lead
 *    shaft line is gone, and sets grip / adapter / labor quantities to the
 *    build's total shaft count. Exits immediately when the cart has no builds.
 *
 * Never sends cart attributes.
 */
(() => {
  if (window.G4LBuildCart) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  let working = false;

  async function getCart() {
    const res = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`cart.js ${res.status}`);
    return res.json();
  }

  async function applyUpdates(updates) {
    if (!Object.keys(updates).length) return null;
    const res = await fetch(`${ROOT}cart/update.js`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ updates })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || `update.js ${res.status}`);
    return data;
  }

  function isCartPage() {
    const cartRoute = (window.theme && window.theme.routes && window.theme.routes.cart) || `${ROOT}cart`;
    return window.location.pathname.replace(/\/$/, '') === cartRoute.replace(/\/$/, '');
  }

  /** Re-renders cart UI using the theme's own cart-items element. */
  async function rerender(cart) {
    if (isCartPage()) {
      // The cart page summary/empty state is simplest to refresh with a reload.
      window.location.reload();
      return;
    }

    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refreshCart === 'function') {
      await cartItems.refreshCart();
    } else {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
    }

    const drawer = document.getElementById('cart-drawer');
    const content = drawer && drawer.querySelector('.drawer__content');
    if (content && cart) content.classList.toggle('drawer__content--flex', cart.item_count === 0);
  }

  /**
   * Groups cart lines by _build_id.
   * lead = the shaft line holding the build specs (_build_role: shaft)
   * shafts = every shaft line in the build (lead + shaft_club lines)
   * parts = grip / adapter / labor / add-on lines (qty must equal the build's total shafts)
   */
  function groupBuilds(cart) {
    const groups = {};
    cart.items.forEach((item) => {
      const props = item.properties || {};
      const id = props._build_id;
      if (!id) return;
      if (!groups[id]) groups[id] = { lead: null, shafts: [], parts: [] };
      const g = groups[id];
      if (props._build_role === 'shaft') { g.lead = item; g.shafts.push(item); }
      else if (props._build_role === 'shaft_club') g.shafts.push(item);
      else g.parts.push(item);
    });
    return groups;
  }

  async function removeBuild(buildId, button) {
    if (working) return;
    working = true;
    const row = button && button.closest('.cart-item');
    const loader = row && row.querySelector('.cart-item__loader');
    const errorEl = row && row.querySelector('.cart-item__error');
    if (loader) loader.hidden = false;
    if (button) button.disabled = true;

    try {
      const cart = await getCart();
      const updates = {};
      cart.items.forEach((item) => {
        if (item.properties && item.properties._build_id === buildId) updates[item.key] = 0;
      });
      const updated = await applyUpdates(updates);
      document.dispatchEvent(new CustomEvent('build:removed', { detail: { buildId, cart: updated } }));
      await rerender(updated);
    } catch (err) {
      console.error('[build] remove failed', err); // eslint-disable-line
      if (errorEl) {
        errorEl.textContent = (window.theme && window.theme.strings && window.theme.strings.cartError) || err.message;
        errorEl.hidden = false;
      }
      if (loader) loader.hidden = true;
      if (button) button.disabled = false;
    } finally {
      working = false;
    }
  }

  async function syncBuilds() {
    if (working) return;
    // Cheap guard: only check the server when the page shows build lines.
    if (!document.querySelector('[data-build-id]')) return;
    working = true;

    try {
      const cart = await getCart();
      const updates = {};

      Object.values(groupBuilds(cart)).forEach((group) => {
        if (!group.lead) {
          // The lead shaft (build specs) is gone: remove the rest of the build.
          group.shafts.concat(group.parts).forEach((line) => { updates[line.key] = 0; });
          return;
        }
        const totalShafts = group.shafts.reduce((n, line) => n + line.quantity, 0);
        group.parts.forEach((part) => {
          if (part.quantity !== totalShafts) updates[part.key] = totalShafts;
        });
      });

      if (Object.keys(updates).length) {
        const updated = await applyUpdates(updates);
        await rerender(updated);
      }
    } catch (err) {
      console.error('[build] sync failed', err); // eslint-disable-line
    } finally {
      working = false;
    }
  }

  // Delegated so it survives the theme re-rendering cart HTML.
  document.addEventListener('click', (evt) => {
    const btn = evt.target.closest('.js-remove-build');
    if (!btn) return;
    evt.preventDefault();
    evt.stopPropagation();
    removeBuild(btn.dataset.buildId, btn);
  }, true);

  const runSync = () => setTimeout(syncBuilds, 300);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runSync, { once: true });
  } else {
    runSync();
  }
  window.addEventListener('pageshow', (evt) => { if (evt.persisted) runSync(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') runSync();
  });

  window.G4LBuildCart = { removeBuild, syncBuilds };
})();
